import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { OAuthService } from 'angular-oauth2-oidc';
import { NotificationService } from './notification.service';
import { HttpStatusCode } from '../enum/http-status-code';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, switchMap, finalize } from 'rxjs/operators';
import { NotificationMessages } from '../messages/notification.messages';
import { extractBackendMessage } from '../utils/error-message.util';

@Injectable({ providedIn: 'root' })
export class HttpInterceptorService implements HttpInterceptor {
  constructor(
    private readonly oauthService: OAuthService,
    private readonly notificationService: NotificationService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.oauthService.getAccessToken();

    //    Attach access token if available
    if (token) {
      request = request.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }

    // Disable all buttons to prevent multiple submissions
    this.toggleButtonDisable(true);

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          // Re-enable buttons after successful response
          this.toggleButtonDisable(false);
        }
        return event;
      }),

      catchError((error: HttpErrorResponse) => {
        // ⭐ FIRST: Skip toast if x-error-handled is present
        if (request.headers.has('x-error-handled')) {
          return throwError(() => error);
        }

        // Handle 401 Unauthorized (token expired or invalid)
        if (error.status === HttpStatusCode.Unauthorized) {
          const refreshToken = this.oauthService.getRefreshToken();

          if (!refreshToken) {
            this.notificationService.showError(
              'You need to log in to continue.',
            );
            this.oauthService.logOut();
            return throwError(() => error);
          }

          return from(this.oauthService.refreshToken()).pipe(
            switchMap(() => {
              const newToken = this.oauthService.getAccessToken();
              const retryReq = request.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next.handle(retryReq);
            }),
            catchError((refreshError) => {
              this.notificationService.showError(
                'Your session has expired. Please log in again.',
              );
              return throwError(() => refreshError);
            }),
          );
        }

        // ⭐ If no x-error-handled: show toast
        this.handleHttpError(error);

        return throwError(() => error);
      }),

      //    Always re-enable buttons even if an error occurs
      finalize(() => this.toggleButtonDisable(false)),
    );
  }

  //   Handle general HTTP errors
  //   Handle general HTTP errors with NotificationMessages
  private handleHttpError(error: HttpErrorResponse) {
    let message = NotificationMessages.common.unexpected;

    // Extract backend validation messages (your structure)
    const backendMessage = extractBackendMessage(error);

    switch (error.status) {
      case HttpStatusCode.Unauthorized:
        message = NotificationMessages.auth.unauthorized;
        break;

      case HttpStatusCode.Forbidden:
        message = NotificationMessages.auth.forbidden;
        break;

      case HttpStatusCode.NotFound:
        message = NotificationMessages.common.notFound;
        break;

      case HttpStatusCode.BadRequest:
        // Use backend validation message if present
        message = backendMessage || NotificationMessages.common.badRequest;
        break;

      case HttpStatusCode.InternalServerError:
        message = NotificationMessages.common.serverError;
        break;

      case 0:
        // If the browser is offline, show a network error; if online, show a generic unexpected error.
        if (navigator.onLine) {
          message = NotificationMessages.common.unexpected;
        } else {
          message = NotificationMessages.common.networkError;
        }
        break;

      default:
        message = backendMessage || NotificationMessages.common.unexpected;
        break;
    }

    // Show the toast only once (global handling)
    this.notificationService.showError(message);
  }

  //   Extract meaningful message from API response
  private extractErrorMessage(error: HttpErrorResponse): string {
    const err = error.error;
    if (!err) return 'An unexpected error occurred';
    if (typeof err === 'string') return err;
    if (err.message) return err.message;
    if (Array.isArray(err.errors)) return err.errors.join(', ');
    if (typeof err === 'object') {
      const values = Object.values(err).flat();
      return typeof values[0] === 'string'
        ? values.join(', ')
        : 'An unexpected error occurred';
    }
    return 'An unexpected error occurred';
  }

  //   Toggle all buttons (useful for blocking UI during requests)
  private toggleButtonDisable(value: boolean) {
    const buttons = document.getElementsByTagName('button');
    for (const button of Array.from(buttons)) {
      button.disabled = value;
    }
  }
}
